import { useEffect, useState } from "react";
import type { MenuRoute, AvailibleMenuData } from "./menuTypes";

/**
 * Internal structure for tracking available menu paths
 */
interface PathMapItem {
  key: string;
  value: MenuRoute;
  isSubMenu?: boolean;
}

/**
 * Result of getPathMap function
 */
interface PathMapResult {
  availableMenuPathsList: PathMapItem[];
  forceAvalibleMenuList: PathMapItem[];
  allPaths: Record<string, MenuRoute>;
}

/**
 * Builds the tree the sidebar actually draws: entries whose `view` is falsy are
 * dropped, and a section that loses all of its children goes with them.
 *
 * Pruning belongs here, not in NavList, so the rendering layer is handed a plain
 * list of items to draw unconditionally — visibility is decided once, by the
 * menu hooks, in each entry's `view` / `disabled`.
 *
 * `allPaths` is deliberately built from the UNPRUNED tree (see below): a hidden
 * entry must still resolve to a menu, or a deep link to it would land nowhere.
 * See MENU_RESOLUTION.md.
 */
const pruneHidden = (routes: MenuRoute[]): MenuRoute[] =>
  routes.reduce<MenuRoute[]>((kept, route) => {
    if (route.collapse) {
      // A section has no page of its own; it survives only while it still has a
      // child to show. `view: false` on the section itself hides it outright.
      if (route.view === false) return kept;
      const declared = route.views?.length ?? 0;
      const views = pruneHidden(route.views ?? []);
      // Sections whose children arrive asynchronously (the dashboard list) start
      // out empty and must not be dropped before they are filled in.
      if (declared > 0 && views.length === 0) return kept;
      kept.push({ ...route, views });
      return kept;
    }
    if (route.view) kept.push(route);
    return kept;
  }, []);

/**
 * Hook to process routes and determine available menu paths based on permissions
 *
 * @param routes - Array of menu route configurations
 * @param defaultPathValue - Default path to use if no available menu paths match
 * @returns Object containing available paths, default path, and menu availability status
 */
const useAvailibleMenu = (
  routes: MenuRoute[] | undefined,
  defaultPathValue = "/tracked_assets"
): AvailibleMenuData => {
  const [data, setData] = useState<AvailibleMenuData>({
    availableMenuPaths: {},
    allPaths: {},
    defaultPath: "",
  });

  const getPathMap = (r: MenuRoute, isRoot: boolean): PathMapResult => {
    let availableMenuPathsList: PathMapItem[] = [];
    let forceAvalibleMenuList: PathMapItem[] = [];
    let allPaths: Record<string, MenuRoute> = {};

    if (r.to) {
      if ((isRoot && r.view !== false) || (!isRoot && r.view)) {
        availableMenuPathsList.push({ key: r.to, value: r });
      }
      if (r.isForceAvailableMenu) {
        forceAvalibleMenuList.push({ key: r.to, value: r });
      }
      allPaths[r.to] = r;
      r.subPages?.forEach((sub) => {
        allPaths[sub] = r;
        availableMenuPathsList.push({ key: sub, value: r, isSubMenu: true });
      });
    } else {
      r.views?.forEach((route) => {
        const {
          availableMenuPathsList: avaliable,
          forceAvalibleMenuList: forceList,
          allPaths: match,
        } = getPathMap(route, false);
        if ((isRoot && r.view !== false) || (!isRoot && r.view)) {
          availableMenuPathsList = [...availableMenuPathsList, ...avaliable];
        }
        forceAvalibleMenuList = [...forceAvalibleMenuList, ...forceList];
        allPaths = { ...allPaths, ...match };
      });
    }

    return {
      availableMenuPathsList,
      forceAvalibleMenuList,
      allPaths,
    };
  };

  useEffect(() => {
    let allPaths: Record<string, MenuRoute> = {};
    let availableMenuPathsList: PathMapItem[] = [];
    let forceAvalibleMenuList: PathMapItem[] = [];
    routes?.forEach((r) => {
      const {
        availableMenuPathsList: avaliable,
        forceAvalibleMenuList: forceList,
        allPaths: match,
      } = getPathMap(r, true);
      availableMenuPathsList = [...availableMenuPathsList, ...avaliable];
      forceAvalibleMenuList = [...forceAvalibleMenuList, ...forceList];
      allPaths = { ...allPaths, ...match };
    });

    const availableMenuPaths: Record<string, MenuRoute> = {};
    availableMenuPathsList.forEach((item) => {
      availableMenuPaths[item.key] = item.value;
    });

    const forceMenuPaths: Record<string, MenuRoute> = {};
    forceAvalibleMenuList.forEach((item) => {
      forceMenuPaths[item.key] = item.value;
    });

    const filterListMenu = availableMenuPathsList?.filter(
      (item) => !item.isSubMenu
    );

    let path = defaultPathValue;
    if (!availableMenuPaths[defaultPathValue] && filterListMenu.length > 0) {
      // use first path
      path = filterListMenu[0].key;
    }

    setData({
      routes,
      // Same tree, minus everything hidden. `routes` keeps its original identity
      // because useHomeMenu compares against it to detect a stale computation.
      renderRoutes: pruneHidden(routes ?? []),
      availableMenuPaths,
      forceMenuPaths,
      allPaths,
      defaultPath: {
        pathname: path,
        search: "",
      },
      isAdminMenuAvailable: filterListMenu.length > 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes]);

  return data;
};

export default useAvailibleMenu;
